import { useState, useEffect, useRef } from 'react';
import { T } from '@tolgee/react';
import { useTolgee } from '@tolgee/react';
import { tolgeeConfig } from './tolgee-config';

/**
 * Utilidad para obtener traducciones de Tolgee
 * @param key - La clave de traducción a usar
 * @param params - Parámetros opcionales para la traducción
 * @returns Componente de traducción Tolgee
 */
export const t = (key: string, params?: Record<string, any>) => {
  return <T keyName={key} params={params} />;
};

/**
 * Hook para obtener y manejar el idioma actual en la aplicación
 * @returns {Object} Objeto con el idioma actual y función para cambiar el idioma
 */
export const useLanguage = () => {
  const tolgee = useTolgee();
  
  // Inicializar con el idioma del localStorage o el configurado por defecto
  const getInitialLanguage = () => {
    if (typeof window !== 'undefined') {
      try {
        return localStorage.getItem('tolgee-language') || tolgeeConfig.language;
      } catch (error) {
        console.warn('Error accessing localStorage:', error);
        return tolgeeConfig.language;
      }
    }
    return tolgeeConfig.language;
  };
  
  // Usar el idioma directamente de Tolgee para forzar re-renderización
  const [language, setLanguage] = useState<string>(() => {
    try {
      return tolgee.getLanguage() || getInitialLanguage();
    } catch {
      return getInitialLanguage();
    }
  });
  const isChangingLanguage = useRef(false);
  const lastChangeTime = useRef(0);

  useEffect(() => {
    // Solo ejecutar en el cliente
    if (typeof window === 'undefined') return;
    
    try {
      // Inicializar el estado con el idioma actual de Tolgee o localStorage
      const currentLang = tolgee.getLanguage();
      const savedLang = localStorage.getItem('tolgee-language');
      
      console.log('🔧 [useLanguage] Inicializando:', { currentLang, savedLang, language });
      
      if (savedLang && savedLang !== currentLang) {
        // Si hay un idioma guardado diferente al actual, lo cambiamos
        console.log('🔧 [useLanguage] Cambiando idioma a guardado:', savedLang);
        tolgee.changeLanguage(savedLang);
        setLanguage(savedLang);
      } else if (currentLang && currentLang !== language) {
        // Si no hay idioma guardado, usamos el actual de Tolgee
        console.log('🔧 [useLanguage] Usando idioma actual de Tolgee:', currentLang);
        setLanguage(currentLang);
      }
    } catch (error) {
      console.error('Error initializing language:', error);
    }

    // Forzar actualización del estado cuando cambie el idioma en Tolgee
    const forceUpdate = () => {
      const currentLang = tolgee.getLanguage();
      if (currentLang && currentLang !== language) {
        console.log('🔧 [useLanguage] Forzando actualización del idioma:', currentLang);
        setLanguage(currentLang);
      }
    };

    // Usar un intervalo más frecuente para detectar cambios
    const intervalId = setInterval(forceUpdate, 500); // Verificar cada 500ms

    // También forzar actualización inmediata
    forceUpdate();

    // Cleanup
    return () => {
      clearInterval(intervalId);
    };
  }, [tolgee]);

  // Escuchar cambios en localStorage para sincronizar entre pestañas
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'tolgee-language' && e.newValue) {
        console.log('🔧 [useLanguage] Idioma cambiado en otra pestaña:', e.newValue);
        setLanguage(e.newValue);
        tolgee.changeLanguage(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [tolgee]);

  /**
   * Cambia el idioma de la aplicación
   * @param lang Código del idioma (ej: 'es', 'en')
   */
  const changeLanguage = async (lang: string) => {
    // Solo ejecutar en el cliente
    if (typeof window === 'undefined') return;
    
    // Evitamos cambios innecesarios - si el idioma ya es el mismo
    if (lang === language) {
      console.log('🔧 [useLanguage] Idioma ya es el mismo:', lang);
      return;
    }
    
    // Protección contra cambios demasiado frecuentes
    const now = Date.now();
    if (now - lastChangeTime.current < 1000) { // Solo permitimos un cambio por segundo
      console.log('🔧 [useLanguage] Cambio demasiado frecuente, ignorando');
      return;
    }
    
    // Evitamos cambios simultáneos
    if (isChangingLanguage.current) {
      console.log('🔧 [useLanguage] Cambio en progreso, ignorando');
      return;
    }
    
    try {
      isChangingLanguage.current = true;
      lastChangeTime.current = now;
      
      console.log('🔧 [useLanguage] Cambiando idioma a:', lang);
      
      // Cambiar idioma en Tolgee
      await tolgee.changeLanguage(lang);
      setLanguage(lang);
      
      // Forzar re-renderización de todos los componentes T
      setTimeout(() => {
        const currentLang = tolgee.getLanguage();
        console.log('🔧 [useLanguage] Verificando idioma después del cambio:', currentLang);
        if (currentLang !== lang) {
          console.log('🔧 [useLanguage] Reintentando cambio de idioma...');
          tolgee.changeLanguage(lang);
        }
      }, 100);
      
      // Guardamos la preferencia del usuario
      try {
        localStorage.setItem('tolgee-language', lang);
        console.log('🔧 [useLanguage] Idioma guardado en localStorage:', lang);
      } catch (error) {
        console.warn('Error saving language to localStorage:', error);
      }
      
      // Notificar a otros componentes del cambio de idioma
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: lang } }));
      
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      // Aseguramos que la bandera siempre se resetea
      isChangingLanguage.current = false;
    }
  };

  return { language, changeLanguage };
};

/**
 * Utilidad para obtener el texto de una traducción (para casos donde no se puede usar JSX)
 * Usar solo en el cliente, ya que depende de APIs del navegador
 * @param key - La clave de traducción
 * @param defaultValue - Valor predeterminado si la traducción no existe
 * @returns String con la traducción
 */
export const getTranslation = ( defaultValue: string = ''): string => {
  // En entorno de cliente podemos usar localStorage para obtener el idioma actual
  if (typeof window !== 'undefined') {
    const lang = localStorage.getItem('tolgee-language') || 'es';
    // Implementación simplificada, en producción debería usar la API de Tolgee para obtener la traducción
    return lang;
  }
  return defaultValue;
};

/**
 * Hook para forzar re-renderización cuando cambie el idioma
 * Úsalo en componentes que usan <T> para asegurar que se actualicen
 */
export const useForceUpdate = () => {
  const [, forceUpdate] = useState({});
  
  useEffect(() => {
    const interval = setInterval(() => {
      // Forzar re-renderización cada vez que se detecte un cambio
      forceUpdate({});
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  return forceUpdate;
}; 